// The sign-in server's password refusals, in words a parent or teacher can act
// on. Its own messages name character sets ("abcdefghijklmnopqrstuvwxyz,
// ABCDEFGHIJKLMNOPQRSTUVWXYZ, ...") or say "weak", and the sign-up page used to
// report every one of them as "at least 8 characters" - so a teacher with a
// long password missing a symbol was told it was too short (12 Oct 2026).
//
// Returns null when the message is not about the password, so the caller can
// fall back to its own wording.
export function explainPasswordError(raw: string | null | undefined): string | null {
  const msg = raw ?? "";
  if (/at least one character of each/i.test(msg)) {
    return "The password needs at least one capital letter, one lowercase letter, one number and one symbol (such as ! # $ or ?).";
  }
  if (/pwned|leaked|breach|known to be weak|easy to guess/i.test(msg)) {
    return "That password has turned up in a data breach on another website, so it can't be used here. Please choose a different one.";
  }
  if (/different from the old password/i.test(msg)) {
    return "That is already the password on this account. Choose one you have not used here before.";
  }
  const length = /at least (\d+) characters/i.exec(msg);
  if (length && /password/i.test(msg)) {
    return `The password must be at least ${length[1]} characters long.`;
  }
  if (/password/i.test(msg) && /weak/i.test(msg)) {
    return "That password is too weak. Use at least 8 characters with a capital letter, a lowercase letter, a number and a symbol.";
  }
  return null;
}

/** Read the `{ error }` body an edge function sent with a non-2xx reply. */
export async function functionErrorBody(error: unknown): Promise<string | null> {
  const res = (error as { context?: Response } | null)?.context;
  if (!res || typeof res.clone !== "function") return null;
  try {
    const body = await res.clone().json();
    return typeof body?.error === "string" ? body.error : null;
  } catch {
    return null;
  }
}
